import { useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";

/** Client Portal v2 (2026-09, Phase 1, §5) — an async two-way notes thread, NOT live chat: no
 * websockets, no typing indicators. The parent polls on an interval (see
 * ClientPortalProject.jsx); a client's own send is rendered optimistically (an instant local
 * bubble marked `pending`) before the server confirms it. Now the FIRST thing in Communication
 * (§5 reorders Messages ahead of Meetings/Updates) on the light content surface. */
export function MessagesThread({ messages, onSend, sending }) {
  const [draft, setDraft] = useState("");
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "nearest" });
  }, [messages.length]);

  const submit = (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || sending) return;
    onSend(text);
    setDraft("");
  };

  return (
    <div data-testid="client-portal-messages-thread">
      {messages.length === 0 ? (
        <p className="text-[13.5px] text-[color:var(--cp-grey-3)] mb-4" data-testid="client-portal-messages-empty">Send a note to your NJM team. They'll reply here.</p>
      ) : (
        <div className="space-y-2.5 mb-4 max-h-[420px] overflow-y-auto pr-1" data-testid="client-portal-messages-list">
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.sender_role === "client" ? "justify-end" : "justify-start"}`} data-testid={`client-portal-message-${m.id}`}>
              <div className={`cp-panel px-4 py-2.5 max-w-[80%] ${m.sender_role === "client" ? "!bg-[color:var(--cp-gold)]/10 !border-[color:var(--cp-gold)]/30" : ""} ${m.pending ? "opacity-60" : ""}`}>
                <div className="text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--cp-grey-4)] mb-0.5">{m.sender_name}{m.pending ? " · sending…" : ""}</div>
                <p className="text-[13px] text-[color:var(--cp-ink-text)] leading-relaxed whitespace-pre-wrap">{m.body}</p>
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      )}
      <form onSubmit={submit} className="flex items-end gap-3" data-testid="client-portal-message-form">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) submit(e); }}
          placeholder="Write a note to your team…"
          rows={2}
          maxLength={4000}
          data-testid="client-portal-message-input"
          className="cp-input-light flex-1 px-4 py-3 text-[13px] resize-none"
        />
        <button type="submit" disabled={!draft.trim() || sending} data-testid="client-portal-message-send-btn" aria-label="Send message"
          className="cp-btn-primary shrink-0 h-11 w-11 flex items-center justify-center transition-opacity">
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
}
